import {
  Box,
  Button,
  Card,
  CardBody,
  ChakraProvider,
  Grid,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Stack,
  Text,
  useMediaQuery,
  useOutsideClick,
  VStack,
} from "@chakra-ui/react";
import { faLongArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { endOfMonth, format, parseISO } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftCircle,
  ArrowRightCircle,
  Check,
  Eye,
  Menu,
  Search,
} from "react-feather";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentProfile } from "../../ContextWrapper";
import { getAdvancedMetricSummary } from "../../api/expenseBreakdown";
import { fallbackDate } from "../../config/dashboardDefaults";
import theme from "../../theme";
import {
  colorExpenseFormat,
  colorFormat,
  colorTargetFormat,
  customCurrencyFormat,
  isGoodValue,
} from "../../utils";
import { getCustomerDashboardDate } from "../../utils/getCustomerDashboardDate";
import AdvancedMetricCircle from "../dashboardComponents/AdvancedMetrics/advancedMetricCircle";
import AdvancedMetricsGraph from "../dashboardComponents/AdvancedMetrics/advancedMetricGraph";
import { AdvancedMetricUpperCard } from "../dashboardComponents/AdvancedMetrics/advancedMetricUpperCard";
import { AdvancedMetricsLowerCard } from "../dashboardComponents/AdvancedMetrics/advancedMetricsLowerCard";
import LucroInsightButton from "../dashboardComponents/lucroInsight";
import YtdToggleCompact from "../dashboardComponents/ytdToggleCompact";
import NotificationContainer from "../generic/updateNotification";
import UncategorizedNotificationBanner from "../uncategorized/uncategorizedNotificationBanner";
import DashboardDropdown from "./dashboardDateComponent";
import ClientDashboardLayout from "./dashboardLayout";
import { VendorTable } from "./vendorAnalytics";
import axios from "axios";
import TargetTypeToggle from "../dashboardComponents/targetToggle";

const tagOptions = [
  { label: "Profit", value: "profit", color: "#38A169" },
  { label: "Income", value: "income", color: "#00B5D8" },
  { label: "Expenses", value: "expense", color: "#E53E3E" },
  { label: "Team", value: "team", color: "#ED8936" },
  { label: "Marketing", value: "marketing", color: "#627CFB" },
  { label: "Operations", value: "overhead", color: "#38B2AC" },
  { label: "Products", value: "cogs", color: "#ED64A6" },
];

const verbMap = {
  income: "is",
  profit: "is",
  expense: "are",
  team: "are",
  marketing: "are",
  overhead: "are",
  cogs: "are",
};

const MetricSearchDropdown = ({ tagOptions, selectedTag, handleChange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef();

  useOutsideClick({
    ref: containerRef,
    handler: () => setShowDropdown(false),
  });

  const filteredTags = useMemo(
    () =>
      tagOptions.filter((tag) =>
        tag.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [searchTerm, tagOptions],
  );

  const handleSelectTag = (tagValue) => {
    handleChange(tagValue);

    const selected = tagOptions.find((t) => t.value === tagValue);

    setSearchTerm(selected?.label || "");

    setShowDropdown(false);
  };

  const handleSearchClick = () => {
    if (filteredTags.length > 0) {
      handleSelectTag(filteredTags[highlightIndex].value);
    }
  };

  return (
    <Box position="relative" width="240px" ref={containerRef}>
      <InputGroup width="100%" height="36px">
        <Input
          height="36px"
          fontSize="14px"
          placeholder="Search Metric Categories..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
            setHighlightIndex(0);
          }}
          onFocus={() => setShowDropdown(true)}
        />
        <InputRightElement width="44px" height="36px">
          <Button
            width="44px"
            height="36px"
            p={0}
            minW="unset"
            borderLeftRadius={0}
            onClick={handleSearchClick}
          >
            <Search size={20} />
          </Button>
        </InputRightElement>
      </InputGroup>

      {showDropdown && filteredTags.length > 0 && (
        <VStack
          position="absolute"
          top="40px"
          left={0}
          right={0}
          bg="white"
          border="1px solid #E0E0E0"
          borderRadius="md"
          shadow="md"
          maxH="200px"
          overflowY="auto"
          zIndex={999}
          spacing={0}
        >
          {filteredTags.map((tag, idx) => (
            <Box
              key={tag.value}
              px={3}
              py={2}
              width="100%"
              cursor="pointer"
              bg={highlightIndex === idx ? "gray.100" : "white"}
              _hover={{ bg: "gray.100" }}
              onMouseEnter={() => setHighlightIndex(idx)}
              onClick={() => handleSelectTag(tag.value)}
            >
              <Text fontSize="14px">{tag.label}</Text>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

const AdvancedMetrics = () => {
  const { profile } = useCurrentProfile();
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [customTarget, setCustomTarget] = useState(
    JSON.parse(localStorage.getItem("isCustomTarget") || "false"),
  );

  useEffect(() => {
    const handleTargetTypeChange = (event) => {
      setCustomTarget(event.detail);
    };

    window.addEventListener("targetTypeChanged", handleTargetTypeChange);

    return () => {
      window.removeEventListener("targetTypeChanged", handleTargetTypeChange);
    };
  }, []);

  useEffect(() => {
    if (!profile) return;

    const storedId = sessionStorage.getItem("selectedCustomerId");
    if (!storedId) return;

    if (profile.type !== "admin") {
      const customer =
        profile.customers.find((c) => c.id.toString() === storedId) ||
        profile.customers[0] ||
        null;
      setActiveCustomer(customer);
      return;
    }

    const fetchCustomer = async () => {
      try {
        const res = await axios.get(
          `/api/v2/customers/${storedId}/`,
          { headers: { "Content-Type": "application/json" } },
          { withCredentials: true },
        );
        setActiveCustomer(res.data);
      } catch (err) {
        console.error("Error fetching admin customer:", err);
      }
    };

    fetchCustomer();
  }, [profile]);

  useEffect(() => {
    const handleSelectedChange = async () => {
      const storedId = sessionStorage.getItem("selectedCustomerId");
      if (!storedId) return;

      if (profile.type !== "admin") {
        const found = profile.customers.find(
          (c) => c.id.toString() === storedId,
        );
        if (found) setActiveCustomer(found);
      } else {
        try {
          const res = await axios.get(`/api/v2/customers/${storedId}/`, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          });
          setActiveCustomer(res.data);
        } catch (err) {
          console.error("Error fetching admin customer:", err);
        }
      }
    };

    window.addEventListener("selectedCustomerId-changed", handleSelectedChange);
    return () => {
      window.removeEventListener(
        "selectedCustomerId-changed",
        handleSelectedChange,
      );
    };
  }, [profile]);

  const customer_type = activeCustomer?.customer_type;
  const customer = activeCustomer;
  const customer_id = activeCustomer?.id;
  const normalizeToEOM = (date) => {
    if (!date) return fallbackDate;
    return endOfMonth(new Date(date));
  };

  const defaultDate = customer?.dashboard_date
    ? normalizeToEOM(parseISO(customer.dashboard_date))
    : fallbackDate;

  const [initialDashDate, setInitialDashDate] = useState(() =>
    normalizeToEOM(getCustomerDashboardDate(customer)),
  );

  const [dashDate, setDashDate] = useState(initialDashDate);

  const [dateCategory, setDateCategory] = useState(() =>
    format(initialDashDate, "yyyy-MM"),
  );

  useEffect(() => {
    if (!activeCustomer) return;

    const stored = localStorage.getItem("dashboardDate");
    const newDate = stored
      ? normalizeToEOM(new Date(stored.split("-").map(Number)))
      : normalizeToEOM(getCustomerDashboardDate(activeCustomer));

    setDashDate(newDate);
    setInitialDashDate(newDate);
    setDateCategory(format(newDate, "yyyy-MM"));
  }, [activeCustomer]);

  useEffect(() => {
    const handleDashboardDateChange = () => {
      if (!activeCustomer) return;

      const stored = localStorage.getItem("dashboardDate");
      const newDate = stored
        ? normalizeToEOM(new Date(stored.split("-").map(Number)))
        : normalizeToEOM(getCustomerDashboardDate(activeCustomer));

      setDashDate(newDate);
      setInitialDashDate(newDate);
      setDateCategory(format(newDate, "yyyy-MM"));
    };

    window.addEventListener("dashboardDate-changed", handleDashboardDateChange);
    return () =>
      window.removeEventListener(
        "dashboardDate-changed",
        handleDashboardDateChange,
      );
  }, [activeCustomer]);

  const updateDate = (newDate) => {
    const normalized = normalizeToEOM(newDate);
    setDashDate(normalized);
    setDateCategory(format(normalized, "yyyy-MM"));
  };
  const formattedDate = format(dashDate, "yyyy-MM-dd");
  const { type: urlType } = useParams();
  const { prevPage } = useParams();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [activeType, setActiveType] = useState(urlType || "profit");
  const [selectedTag, setSelectedTag] = useState(
    tagOptions.find((tag) => tag.value === activeType)?.value ||
      tagOptions[0].value,
  );

  const [metricSummary, setMetricSummary] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [adjustedView, setAdjustedView] = useState(() => {
    const stored = sessionStorage.getItem("adjustedView");
    return stored ? JSON.parse(stored) : false;
  });

  const [ytd, setYtd] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("ytd") || "false");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = () => {
      const ytd = JSON.parse(sessionStorage.getItem("ytd") || "false");
      setYtd(ytd);
    };

    window.addEventListener("ytd-changed", handler);
    return () => window.removeEventListener("ytd-changed", handler);
  }, []);

  useEffect(() => {
    setIsMobileDevice(
      /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    );
  }, [setIsMobileDevice]);

  const handleToggleAdjusted = () => {
    setAdjustedView((prev) => {
      const next = !prev;
      sessionStorage.setItem("adjustedView", JSON.stringify(next));

      window.dispatchEvent(
        new CustomEvent("adjustedView-changed", { detail: next }),
      );

      return next;
    });
  };

  useEffect(() => {
    const handler = (e) => {
      setAdjustedView(e.detail);
    };

    window.addEventListener("adjustedView-changed", handler);

    return () => window.removeEventListener("adjustedView-changed", handler);
  }, []);

  const getDirection = (value) => (value > 0 ? "higher" : "lower");
  const getChange = (value) => (value > 0 ? "increase" : "decrease");
  const getAboveBelow = (value) => (value > 0 ? "above" : "below");

  const getLabel = (value) =>
    tagOptions.find((t) => t.value === value)?.label.toLowerCase() || value;

  const getTileText = (tileNum, selectedTag, metricSummary) => {
    const label = getLabel(selectedTag);
    const verb = verbMap[selectedTag] || "is";
    const val = (v) => Math.abs(v ?? 0);
    const fmtCurrnecy = (v) =>
      customCurrencyFormat(v, false, false, true, false, false, false, true);
    const getSignDirection = (val) => {
      const num = parseFloat(val?.toString().replace(/[^0-9.-]+/g, ""));
      if (isNaN(num)) return "-";
      return num >= 0 ? "higher" : "lower";
    };
    const additionalWord =
      selectedTag === "income" ||
      selectedTag === "expense" ||
      selectedTag === "profit"
        ? ""
        : "costs";

    const changePct = (v) =>
      v != null && !isNaN(val(v)) ? `${val(v).toFixed(2)}%` : "-";

    const safeCurrency = (v) =>
      v !== null && v !== undefined ? fmtCurrnecy(v) : "-";
    const safePct = (v) => (v !== null && v !== undefined ? changePct(v) : "-");
    const safeAboveBelow = (v) =>
      v !== null && v !== undefined ? getAboveBelow(v) : "-";

    const pnlIncome = safeCurrency(metricSummary?.perfect_pnl_income);
    const pnlExpense = safeCurrency(metricSummary?.perfect_pnl_expense);
    const income = safeCurrency(metricSummary?.perfect_pnl_income_actual);
    const pnlIncomeDiff = safePct(metricSummary?.perfect_pnl_income_diff);
    const costs = safeCurrency(metricSummary?.perfect_pnl_expense_actual);
    const pnlPercent = safePct(metricSummary?.pnl_value);
    const pnlExpenseDiff = safePct(metricSummary?.perfect_pnl_expense_diff);
    const currentDiff = safeCurrency(
      metricSummary?.current_month_target_change,
    );
    const nextDiff = safeCurrency(metricSummary?.next_month_target_change);

    const perfectProfit = safeCurrency(metricSummary?.perfect_profit);
    const perfectProfitMargin = safePct(metricSummary?.perfect_pl_profit);

    const aboveBelowIncome = safeAboveBelow(
      metricSummary?.perfect_pnl_income_diff,
    );
    const aboveBelowExpense = safeAboveBelow(
      metricSummary?.perfect_pnl_expense_diff,
    );
    const aboveBelowCurrent = safeAboveBelow(
      metricSummary?.current_month_target_change,
    );
    const aboveBelowNext = safeAboveBelow(
      metricSummary?.next_month_target_change,
    );

    switch (tileNum) {
      case 1:
        return `This month’s ${label} ${verb} ${fmtCurrnecy(
          metricSummary?.current_month_target_change,
        )} ${getDirection(
          metricSummary?.current_target_change_pct,
        )} than the target, indicating a ${changePct(
          metricSummary?.current_target_change_pct,
        )} ${getChange(metricSummary?.current_target_change_pct)}.`;

      case 2:
        if (selectedTag === "profit") {
          return `At the current income level, profit should be ${fmtCurrnecy(
            metricSummary?.perfect_pnl,
          )}. This is ${fmtCurrnecy(
            metricSummary?.perfect_pnl_change,
          )} ${getAboveBelow(
            metricSummary?.pnl_difference,
          )} the Perfect P&L target.`;
        } else if (selectedTag === "income") {
          return `At the current income level, total expenses should be under ${fmtCurrnecy(
            metricSummary?.perfect_pnl,
          )}. Current expenses are ${fmtCurrnecy(
            metricSummary?.perfect_pnl_change,
          )} ${getAboveBelow(
            metricSummary?.pnl_difference,
          )} the Perfect P&L expense target.`;
        } else if (selectedTag === "expense") {
          return `At the current expense level, income should exceed ${fmtCurrnecy(
            metricSummary?.perfect_pnl,
          )}. Current income is ${fmtCurrnecy(
            metricSummary?.perfect_pnl_change,
          )} ${getAboveBelow(
            metricSummary?.pnl_difference,
          )} the Perfect P&L income target.`;
        } else {
          return `At the current ${label} expense level, income should exceed ${fmtCurrnecy(
            metricSummary?.perfect_pnl,
          )}. Current income is ${fmtCurrnecy(
            metricSummary?.perfect_pnl_change,
          )} ${getAboveBelow(
            metricSummary?.pnl_difference,
          )} the Perfect P&L income expectation for that spend.`;
        }

      case 3:
        return `Current ${label} ${verb} ${fmtCurrnecy(
          metricSummary?.next_month_target_change || "-",
        )} ${getAboveBelow(
          metricSummary?.next_month_target_change_pct,
        )} next month’s target, requiring a ${getChange(
          metricSummary?.next_month_target_change_pct,
        )} of ${changePct(
          metricSummary?.next_month_target_change_pct,
        )} to stay on track.`;

      case 4:
        return `This month’s ${label} ${additionalWord} ${verb} ${fmtCurrnecy(
          metricSummary?.change_from_last_month || "-",
        )} ${getSignDirection(
          metricSummary?.change_from_last_month,
        )} compared to last month’s, indicating a ${changePct(
          metricSummary?.change_from_last_month_pct,
        )} ${getChange(metricSummary?.change_from_last_month_pct)}.`;

      case 5:
        return `This month’s ${label} ${additionalWord} ${verb} ${fmtCurrnecy(
          metricSummary?.change_from_ytd_avg || "-",
        )} ${getSignDirection(
          metricSummary?.change_from_ytd_avg,
        )} compared to the YTD average, indicating a ${changePct(
          metricSummary?.change_from_ytd_avg_pct,
        )} ${getChange(metricSummary?.change_from_ytd_avg)}.`;

      case 6:
        return `This month’s ${label} ${additionalWord} ${verb} ${fmtCurrnecy(
          metricSummary?.change_from_last_ytd_avg || "-",
        )} ${getSignDirection(
          metricSummary?.change_from_last_ytd_avg,
        )} compared to last year’s average, indicating a ${changePct(
          metricSummary?.change_from_last_ytd_avg_pct,
        )} ${getChange(metricSummary?.change_from_last_ytd_avg_pct)}.`;

      case 7:
        return `This month’s ${label} ${additionalWord} ${verb} ${fmtCurrnecy(
          metricSummary?.change_last_three_month_avg || "-",
        )} ${getSignDirection(
          metricSummary?.change_last_three_month_avg,
        )} compared to the last three month average, indicating a ${changePct(
          metricSummary?.change_last_three_month_avg_pct,
        )} ${getChange(metricSummary?.change_last_three_month_avg_pct)}.`;

      case 8:
        if (!metricSummary) return "";

        switch (selectedTag) {
          case "profit":
          case "income":
            return `To reach the Perfect P&L Profit of ${perfectProfit}, with current expenses at ${costs}, income must exceed ${pnlIncome}. Current income is ${pnlIncomeDiff} ${aboveBelowIncome} this benchmark.`;

          case "expense":
            return `With Expenses at ${costs}, income must exceed ${pnlIncome} to deliver the ideal profit margin of ${perfectProfitMargin}. Current income is ${pnlIncomeDiff} ${aboveBelowIncome} this benchmark.`;

          case "team":
            return `With Team costs at ${costs}, income must reach at least ${pnlIncome} to meet the ideal team-to-income ratio of ${pnlPercent}. Current income is ${pnlIncomeDiff} ${aboveBelowIncome} this benchmark.`;

          case "marketing":
            return `With Marketing costs at ${costs}, income must reach at least ${pnlIncome} to meet the ideal marketing-to-income ratio of ${pnlPercent}. Current income is ${pnlIncomeDiff} ${aboveBelowIncome} this benchmark.`;

          case "overhead":
            return `With Operational costs at ${costs}, income must reach at least ${pnlIncome} to meet the ideal operations-to-income ratio of ${pnlPercent}. Current income is ${pnlIncomeDiff} ${aboveBelowIncome} this benchmark.`;

          case "cogs":
            return `With Product costs at ${costs}, income must reach at least ${pnlIncome} to meet the ideal product-to-income ratio of ${pnlPercent}. Current income is ${pnlIncomeDiff} ${aboveBelowIncome} this benchmark.`;

          default:
            return "";
        }

      case 9:
        if (!metricSummary) return "";

        switch (selectedTag) {
          case "profit":
          case "income":
            return `To reach the Perfect P&L Profit of ${perfectProfit}, with current income at ${income}, expenses must not exceed ${pnlExpense}. Current expenses are ${pnlExpenseDiff} ${aboveBelowExpense} this benchmark.`;

          case "expense":
            return `With Income at ${income}, expenses must not exceed ${pnlExpense} to deliver the ideal profit margin of ${perfectProfitMargin}. Current expenses are ${pnlExpenseDiff} ${aboveBelowExpense} this benchmark.`;

          case "team":
            return `With Income at ${income}, Team costs must not exceed ${pnlExpense} to meet the ideal team-to-income ratio of ${pnlPercent}. Current Team costs are ${pnlExpenseDiff} ${aboveBelowExpense} this benchmark.`;

          case "marketing":
            return `With Income at ${income}, Marketing costs must not exceed ${pnlExpense} to meet the ideal marketing-to-income ratio of ${pnlPercent}. Current Marketing costs are ${pnlExpenseDiff} ${aboveBelowExpense} this benchmark.`;

          case "overhead":
            return `With Income at ${income}, Operational costs must not exceed ${pnlExpense} to meet the ideal operations-to-income ratio of ${pnlPercent}. Current Operational costs are ${pnlExpenseDiff} ${aboveBelowExpense} this benchmark.`;

          case "cogs":
            return `With Income at ${income}, Product costs must not exceed ${pnlExpense} to meet the ideal product-to-income ratio of ${pnlPercent}. Current Product costs are ${pnlExpenseDiff} ${aboveBelowExpense} this benchmark.`;

          default:
            return "";
        }

      case 10:
        if (!metricSummary) return "";

        switch (selectedTag) {
          case "profit":
            return `${
              ytd ? "YTD" : "This month’s"
            } Profit is ${currentDiff} ${aboveBelowCurrent} the ${
              ytd ? "YTD" : "current month’s"
            } target, ${
              aboveBelowCurrent === "above"
                ? "reflecting stronger-than-expected performance."
                : "highlighting a shortfall against planned performance."
            }`;

          case "income":
            return `${
              ytd ? "YTD" : "This month’s"
            } Income is ${currentDiff} ${aboveBelowCurrent} the ${
              ytd ? "YTD" : "current month’s"
            } target, ${
              aboveBelowCurrent === "above"
                ? "reflecting stronger-than-expected performance."
                : "highlighting a shortfall against planned performance."
            }`;

          case "expense":
            return `${
              ytd ? "YTD" : "This month’s"
            } Expenses are ${currentDiff} ${aboveBelowCurrent} the ${
              ytd ? "YTD" : "current month’s"
            } target, ${
              aboveBelowCurrent === "above"
                ? "directly affecting overall profitability."
                : "reflecting disciplined cost control this month."
            }`;

          case "team":
            return `${
              ytd ? "YTD" : "This month’s"
            } Team costs are ${currentDiff} ${aboveBelowCurrent} the ${
              ytd ? "YTD" : "current month’s"
            } target, ${
              aboveBelowCurrent === "above"
                ? "which may impact overall profitability if not addressed."
                : "reflecting efficient resource allocation and strong cost control."
            }`;

          case "marketing":
            return `${
              ytd ? "YTD" : "This month’s"
            } Marketing costs are ${currentDiff} ${aboveBelowCurrent} the ${
              ytd ? "YTD" : "current month’s"
            } target, ${
              aboveBelowCurrent === "above"
                ? "which may impact overall profitability if not addressed."
                : "reflecting efficient resource allocation and strong cost control."
            }`;

          case "overhead":
            return `${
              ytd ? "YTD" : "This month’s"
            } Operations costs are ${currentDiff} ${aboveBelowCurrent} the ${
              ytd ? "YTD" : "current month’s"
            } target, ${
              aboveBelowCurrent === "above"
                ? "which may impact overall profitability if not addressed."
                : "reflecting strong cost control."
            }`;

          case "cogs":
            return `${
              ytd ? "YTD" : "This month’s"
            } Product costs are ${currentDiff} ${aboveBelowCurrent} the ${
              ytd ? "YTD" : "current month’s"
            } target, ${
              aboveBelowCurrent === "above"
                ? "which may impact overall profitability if not addressed."
                : "reflecting strong cost control."
            }`;

          default:
            return "";
        }

      case 11:
        if (!metricSummary) return "";

        switch (selectedTag) {
          case "profit":
            return `This month’s Profit is ${nextDiff} ${aboveBelowNext} the next month’s target, ${
              aboveBelowNext === "above"
                ? "a strong lead heading into the new month."
                : "highlighting the shortfall that must be addressed to stay on track."
            }`;

          case "income":
            return `This month’s Income is ${nextDiff} ${aboveBelowNext} the next month’s target, ${
              aboveBelowNext === "above"
                ? "a strong lead heading into the new month."
                : "highlighting the shortfall that must be addressed to stay on track."
            }`;

          case "expense":
            return `This month’s Expenses are ${nextDiff} ${aboveBelowNext} the next month’s target, ${
              aboveBelowNext === "above"
                ? "signaling a need to reduce costs to meet upcoming targets."
                : "an encouraging sign for maintaining profitability."
            }`;

          case "team":
            return `This month’s Team costs are ${nextDiff} ${aboveBelowNext} the next month’s target, ${
              aboveBelowNext === "above"
                ? "suggesting a need to review efficiency and future cost alignment."
                : "a strong position to maintain efficiency moving forward."
            }`;

          case "marketing":
            return `This month’s Marketing costs are ${nextDiff} ${aboveBelowNext} the next month’s target, ${
              aboveBelowNext === "above"
                ? "suggesting a need to review efficiency and future cost alignment."
                : "a strong position to maintain efficiency moving forward."
            }`;

          case "overhead":
            return `This month’s Operations costs are ${nextDiff} ${aboveBelowNext} the next month’s target, ${
              aboveBelowNext === "above"
                ? "highlighting a potential requirement to realign future cost."
                : "a strong position to maintain efficiency moving forward."
            }`;

          case "cogs":
            return `This month’s Product costs are ${nextDiff} ${aboveBelowNext} the next month’s target, ${
              aboveBelowNext === "above"
                ? "highlighting a potential requirement to realign future cost."
                : "a strong position to maintain efficiency moving forward."
            }`;

          default:
            return "";
        }

      default:
        return "";
    }
  };

  const adjusted = JSON.parse(
    sessionStorage.getItem("adjustedView") || "false",
  );

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchSummary = async () => {
      try {
        const res = await getAdvancedMetricSummary(
          customer_id,
          formattedDate,
          selectedTag,
          controller.signal,
          { adjusted, ytd, customTarget },
        );
        if (isMounted) {
          setMetricSummary(res.data);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Error fetching metric summary:", err);
        }
      }
    };

    if (customer_id && formattedDate && selectedTag) {
      fetchSummary();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, selectedTag, adjusted, ytd, customTarget]);

  useEffect(() => {
    setActiveType(urlType || "profit");
  }, [urlType]);

  const handleChange = (value) => {
    setSelectedTag(value);
  };

  useEffect(() => {
    if (activeType) {
      const matchedTag = tagOptions.find((tag) => tag.value === activeType);
      if (matchedTag) {
        setSelectedTag(matchedTag.value);
      }
    }
  }, [activeType]);

  const scrollTabs = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 150;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const stored = sessionStorage.getItem("adjustedView");
    const parsed = stored ? JSON.parse(stored) : false;
    if (parsed === false) {
      setAdjustedView(false);
    }
  }, [customer_id, adjusted]);

  useEffect(() => {
    const handleSidebarExpand = () => {
      setExpanded(true);
    };

    const handleSidebarCollapse = () => {
      setExpanded(false);
    };

    window.addEventListener("sidebar:expand", handleSidebarExpand);
    window.addEventListener("sidebar:collapse", handleSidebarCollapse);

    return () => {
      window.removeEventListener("sidebar:expand", handleSidebarExpand);
      window.removeEventListener("sidebar:collapse", handleSidebarCollapse);
    };
  }, []);

  const [isMobile] = useMediaQuery("(max-width: 1280px)", { ssr: false });
  const [stackingBreak] = useMediaQuery("(max-width: 1023px)", { ssr: false });
  const [navOverlap] = useMediaQuery("(max-width: 1280px)", { ssr: false });
  const [shiftToggle] = useMediaQuery("(max-width: 720px)", { ssr: false });

  return (
    <ClientDashboardLayout>
      <ChakraProvider theme={theme}>
        <Stack
          bg="#F4F4F2"
          minH={"100vh"}
          maxHeight={"100%"}
          h={"100%"}
          overflowX={"clip"}
        >
          {isMobile && (
            <Card
              position="fixed"
              zIndex={302}
              borderRadius={0}
              h="72px"
              borderBottom="1px solid #e0e0e0"
              width="100%"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                h={"100%"}
                w="100%"
                px={4}
              >
                <Button
                  bg="white"
                  _hover={{ color: "white" }}
                  _active={{ color: "white" }}
                  w="50px"
                  h="50px"
                  p={2}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <Menu
                    style={{ width: "50px", height: "100%" }}
                    color="black"
                  />
                </Button>

                <HStack align="center" spacing="10px">
                  <DashboardDropdown
                    key={dashDate?.toISOString()}
                    height="36px"
                    setEnd={updateDate}
                    dateFromNav={defaultDate}
                    endDate={dashDate}
                    category={dateCategory}
                    setCategory={setDateCategory}
                    monthAmount={customer_type === "trial" ? 3 : 12}
                  />

                  {activeCustomer?.is_demo === false && (
                    <TargetTypeToggle></TargetTypeToggle>
                  )}
                  {(profile?.type === "admin" || customer_type === "trial") && (
                    <IconButton
                      aria-label="Toggle Adjusted View"
                      icon={
                        adjustedView ? <Check size={18} /> : <Eye size={18} />
                      }
                      onClick={handleToggleAdjusted}
                      variant={adjustedView ? "solid" : "outline"}
                      size="20px"
                      height="36px"
                      width="36px"
                    />
                  )}
                </HStack>
              </HStack>
            </Card>
          )}
          {!isMobile && (
            <Card
              position="fixed"
              mt={isMobile ? "72px" : "0px"}
              borderRadius={0}
              width={isMobile ? "100%" : "auto"}
              height="75px"
              display="flex"
              alignItems="center"
              zIndex={600}
              borderBottom="1px solid #e0e0e0"
              mb={1}
            >
              <Grid
                templateColumns="1fr 200fr 1fr"
                width="100%"
                maxW={"100%"}
                alignItems="center"
                padding="0px 20px"
                height="100%"
              >
                <MetricSearchDropdown
                  tagOptions={tagOptions} // all your tag options
                  selectedTag={selectedTag} // current selected tag
                  handleChange={handleChange} // function to switch the selected tag
                />
                {!shiftToggle && (
                  <Box
                    gridRow={1}
                    gridColumn={4}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={"12px"}
                  >
                    <DashboardDropdown
                      key={dashDate?.toISOString()}
                      height="36px"
                      setEnd={updateDate}
                      dateFromNav={defaultDate}
                      endDate={dashDate}
                      category={dateCategory}
                      setCategory={setDateCategory}
                      monthAmount={customer_type === "trial" ? 3 : 12}
                      includeAllFromMin={customer_type === "trial" ? false : true}
                    />
                    <YtdToggleCompact />

                    <LucroInsightButton
                      metricName={
                        tagOptions.find(
                          (option) => option.value === selectedTag,
                        )?.label
                      }
                      selectedTag={selectedTag}
                      customer_id={customer_id}
                      formattedDate={formattedDate}
                    />

                    {activeCustomer?.is_demo === false && (
                      <Box>
                        <TargetTypeToggle></TargetTypeToggle>
                      </Box>
                    )}
                    {(profile?.type === "admin" ||
                      customer_type === "trial") && (
                      <IconButton
                        aria-label="Toggle Adjusted View"
                        icon={
                          adjustedView ? <Check size={18} /> : <Eye size={18} />
                        }
                        onClick={handleToggleAdjusted}
                        variant={adjustedView ? "solid" : "outline"}
                        size="sm"
                        height="36px"
                        width="36px"
                      />
                    )}
                  </Box>
                )}
              </Grid>
            </Card>
          )}

          <Box
            backgroundColor={"#F4F4F2"}
            position="fixed"
            zIndex={200}
            p={{
              base: "12px 24px",
              sm: "12px 24px",
              md: "22px 24x",
              lg: "24px 24px",
            }}
            width={isMobile ? "100%" : "auto"}
            minWidth="100%"
            maxW="100%"
            mt={isMobile ? "72px" : "72px"}
            sx={{
              WebkitMaskImage:
                "linear-gradient(to bottom, black 90%, transparent 100%)",
              maskImage:
                "linear-gradient(to bottom, black 90%, transparent 100%)",
            }}
          >
            {prevPage && (
              <Text
                textColor="#536DEC"
                cursor="pointer"
                fontSize="16px"
                width="180px"
                mb={{
                  base: "2px",
                  sm: "2px",
                  md: "2px",
                  lg: "10px",
                }}
                mt={{
                  base: "2px",
                  sm: "2px",
                  md: "2px",
                  lg: "5px",
                }}
                onClick={() => {
                  if (prevPage === "growth") {
                    navigate("/growth-plan");
                  } else if (prevPage === "performance") {
                    navigate("/performance-review/metric");
                  }
                }}
              >
                <FontAwesomeIcon icon={faLongArrowLeft} strokeWidth={0.1} />{" "}
                Back to{" "}
                {prevPage === "performance" ? "Metric View" : "Growth Plan"}
              </Text>
            )}
            <HStack pb="10px" width="100%" justifyContent="space-between">
              <Text
                fontWeight={700}
                fontSize={{
                  base: "20px",
                  sm: "20px",
                  md: "20px",
                  lg: "24px",
                }}
              >
                Lucro Metrics
              </Text>

              {!isMobileDevice && (
                <HStack
                  pr="10px"
                  mr={
                    !isMobile
                      ? expanded
                        ? navOverlap
                          ? "0px"
                          : "300px"
                        : "105px"
                      : "0px"
                  }
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    transition="all 0.2s ease"
                    cursor="pointer"
                    _hover={{ bg: "gray.100", transform: "scale(1.05)" }}
                    _active={{ bg: "gray.200", transform: "scale(0.95)" }}
                    onClick={() => scrollTabs("left")}
                  >
                    <ArrowLeftCircle size={32} color="#536DEC" />
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    transition="all 0.2s ease"
                    cursor="pointer"
                    _hover={{ bg: "gray.100", transform: "scale(1.05)" }}
                    _active={{ bg: "gray.200", transform: "scale(0.95)" }}
                    onClick={() => scrollTabs("right")}
                  >
                    <ArrowRightCircle size={32} color="#536DEC" />
                  </Box>
                </HStack>
              )}
            </HStack>

            <Grid
              templateColumns="1fr"
              width={
                isMobile
                  ? "100%"
                  : expanded
                    ? navOverlap
                      ? "100%"
                      : "calc(100% - 300px)"
                    : "calc(100% - 105px)"
              }
              height="100%"
            >
              <Box
                ref={scrollRef}
                overflowX="auto"
                whiteSpace="nowrap"
                flex="1"
                sx={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                <HStack as="div" spacing="15px">
                  {tagOptions.map((tag) => (
                    <Button
                      key={tag.value}
                      flexShrink={0}
                      fontWeight={600}
                      padding="10px 15px"
                      borderRadius="md"
                      fontSize={{
                        base: "14px",
                        sm: "14px",
                        md: "16px",
                        lg: "16px",
                      }}
                      width={{
                        base: "100px",
                        sm: "100px",
                        md: "147px",
                        lg: "157px",
                      }}
                      textAlign={"center"}
                      bg={selectedTag === tag.value ? "#536DEC" : "white"}
                      color={selectedTag === tag.value ? "white" : "black"}
                      _hover={{ bg: "#536DEC", color: "white" }}
                      onClick={() => handleChange(tag.value)}
                    >
                      {tag.label}
                    </Button>
                  ))}
                </HStack>
              </Box>
            </Grid>
          </Box>
          <Stack
            backgroundColor={"#F4F4F2"}
            p={"5px 20px"}
            mt={
              isMobile
                ? prevPage
                  ? "224px"
                  : "180px"
                : prevPage
                  ? "240px"
                  : "200px"
            }
          >
            <NotificationContainer rounded={true} p={"10px 0px 2px"} />
            {profile?.type === "client" && (
              <UncategorizedNotificationBanner customerId={customer_id} />
            )}

            {!stackingBreak && (
              <Grid
                templateColumns="2fr 248px 2fr"
                rowGap="0"
                columnGap="10px"
                p="10px 5px 0px 5px"
                alignItems="center"
                justifyItems="center"
              >
                <Stack
                  width="100%"
                  align="center"
                  gridColumn={1}
                  gridRow={1}
                  py="10px"
                >
                  <AdvancedMetricUpperCard
                    titleLeft="Perfect P&L Income"
                    titleRight={ytd ? "YTD Actual Income" : "Actual Income"}
                    target={metricSummary?.perfect_pnl_income}
                    actual={metricSummary?.perfect_pnl_income_actual}
                    changePercent={metricSummary?.perfect_pnl_income_diff}
                    percentageColor={colorFormat(
                      metricSummary?.perfect_pnl_income_diff,
                      false,
                    )}
                    symbol={isGoodValue(metricSummary?.perfect_pnl_income_diff)}
                    image={
                      "/streamline_money-graph-arrow-increase-ascend-growth-up-arrow-stats-graph-right-grow.png"
                    }
                    detailInfo={getTileText(8, selectedTag, metricSummary)}
                  />
                </Stack>

                <Stack
                  width="100%"
                  align="center"
                  gridColumn={1}
                  gridRow={2}
                  py="10px"
                >
                  <AdvancedMetricUpperCard
                    titleLeft="Perfect P&L Expense"
                    titleRight={`${ytd ? "YTD" : ""} ${
                      selectedTag === "income" ||
                      selectedTag === "expense" ||
                      selectedTag === "profit"
                        ? "Actual"
                        : tagOptions.find((opt) => opt.value === selectedTag)
                            ?.label
                    } Expenses`}
                    target={metricSummary?.perfect_pnl_expense}
                    actual={metricSummary?.perfect_pnl_expense_actual}
                    changePercent={metricSummary?.perfect_pnl_expense_diff}
                    percentageColor={colorFormat(
                      metricSummary?.perfect_pnl_expense_diff,
                      true,
                    )}
                    symbol={isGoodValue(
                      metricSummary?.perfect_pnl_expense_diff,
                    )}
                    image={
                      "/streamline_money-graph-arrow-decrease-down-stats-graph-descend-right-arrow.png"
                    }
                    detailInfo={getTileText(9, selectedTag, metricSummary)}
                  />
                </Stack>

                <Stack
                  align="center"
                  gridColumn={2}
                  gridRow="span 2"
                  width="320px"
                >
                  <AdvancedMetricCircle
                    title={
                      tagOptions.find((opt) => opt.value === selectedTag)
                        ?.label || ""
                    }
                    label={
                      selectedTag === "income" ? "Target:" : "Perfect P&L:"
                    }
                    profit={customCurrencyFormat(metricSummary?.actual)}
                    profitPercentage={customCurrencyFormat(
                      metricSummary?.main_percentage,
                      true,
                    )}
                    perfectPL={customCurrencyFormat(
                      metricSummary?.pnl_value,
                      selectedTag === "income" ? false : true,
                    )}
                    color={
                      selectedTag !== "income"
                        ? colorExpenseFormat(
                            metricSummary?.pnl_value,
                            metricSummary?.main_percentage,
                            selectedTag === "profit" ? true : false,
                            true,
                          )
                        : colorTargetFormat(
                            metricSummary?.pnl_value,
                            metricSummary?.main_percentage,
                            true,
                            false,
                          )
                    }
                    status={true}
                    data={metricSummary}
                    invert={
                      selectedTag === "income" || selectedTag === "profit"
                        ? false
                        : true
                    }
                  />
                </Stack>

                <Stack
                  width="100%"
                  align="center"
                  gridColumn={3}
                  gridRow={1}
                  py="10px"
                >
                  <AdvancedMetricUpperCard
                    titleLeft={ytd ? "YTD Target" : "Current Month's Target"}
                    titleRight={ytd ? "YTD Actual Amount" : "Actual Amount"}
                    target={metricSummary?.current_month_target}
                    actual={metricSummary?.actual}
                    changePercent={metricSummary?.current_target_change_pct}
                    percentageColor={colorFormat(
                      metricSummary?.current_target_change_pct,
                      selectedTag === "profit" || selectedTag === "income"
                        ? false
                        : true,
                    )}
                    symbol={isGoodValue(
                      metricSummary?.current_target_change_pct,
                    )}
                    image={"/hugeicons_target-dollar.png"}
                    detailInfo={getTileText(10, selectedTag, metricSummary)}
                  />
                </Stack>

                <Stack
                  width="100%"
                  align="center"
                  gridColumn={3}
                  gridRow={2}
                  py="10px"
                >
                  <AdvancedMetricUpperCard
                    titleLeft="Next Month's Target"
                    titleRight={"Current Month Amount"}
                    target={metricSummary?.next_month_target}
                    actual={metricSummary?.current_month_total}
                    changePercent={metricSummary?.next_month_target_change_pct}
                    percentageColor={colorFormat(
                      metricSummary?.next_month_target_change_pct,
                      selectedTag === "profit" || selectedTag === "income"
                        ? false
                        : true,
                    )}
                    symbol={isGoodValue(
                      metricSummary?.next_month_target_change_pct,
                    )}
                    image={"/tabler_calendar-dollar.png"}
                    detailInfo={getTileText(11, selectedTag, metricSummary)}
                  />
                </Stack>
              </Grid>
            )}

            {stackingBreak && (
              <VStack align="center" justify="center" w="100%" h="100%">
                <Stack width="100%" align="center" mb={"15px"}>
                  <AdvancedMetricCircle
                    title={
                      tagOptions.find((opt) => opt.value === selectedTag)
                        ?.label || ""
                    }
                    label={
                      selectedTag === "income" || selectedTag === "expense"
                        ? "Target:"
                        : "Perfect P&L:"
                    }
                    profit={customCurrencyFormat(metricSummary?.actual)}
                    profitPercentage={customCurrencyFormat(
                      metricSummary?.main_percentage,
                      true,
                    )}
                    perfectPL={customCurrencyFormat(
                      metricSummary?.pnl_value,
                      selectedTag === "income" || selectedTag === "expense"
                        ? false
                        : true,
                    )}
                    color={
                      selectedTag !== "income" && selectedTag !== "expense"
                        ? colorExpenseFormat(
                            metricSummary?.pnl_value,
                            metricSummary?.main_percentage,
                            selectedTag === "profit" ? true : false,
                            true,
                          )
                        : colorTargetFormat(
                            metricSummary?.pnl_value,
                            metricSummary?.main_percentage,
                            true,
                            selectedTag === "expense",
                          )
                    }
                    status={true}
                    data={metricSummary}
                    invert={
                      selectedTag === "income" || selectedTag === "profit"
                        ? false
                        : true
                    }
                  />
                </Stack>
                <Grid
                  w="100%"
                  h="100%"
                  templateColumns={{
                    base: "1fr",
                    md: "repeat(2, 1fr)",
                  }}
                  spacing={2}
                  gap={4}
                >
                  <Stack width="100%" align="center">
                    <AdvancedMetricUpperCard
                      titleLeft="Perfect P&L Income"
                      titleRight={ytd ? "YTD Actual Income" : "Actual Income"}
                      target={metricSummary?.perfect_pnl_income}
                      actual={metricSummary?.perfect_pnl_income_actual}
                      changePercent={metricSummary?.perfect_pnl_income_diff}
                      percentageColor={colorFormat(
                        metricSummary?.perfect_pnl_income_diff,
                        false,
                      )}
                      symbol={isGoodValue(
                        metricSummary?.perfect_pnl_income_diff,
                      )}
                      image={
                        "/streamline_money-graph-arrow-increase-ascend-growth-up-arrow-stats-graph-right-grow.png"
                      }
                      detailInfo={getTileText(8, selectedTag, metricSummary)}
                    />
                  </Stack>
                  <Stack width="100%" align="center">
                    <AdvancedMetricUpperCard
                      titleLeft="Perfect P&L Expense"
                      titleRight={`${ytd ? "YTD" : ""} ${
                        selectedTag === "income" ||
                        selectedTag === "expense" ||
                        selectedTag === "profit"
                          ? "Actual"
                          : tagOptions.find((opt) => opt.value === selectedTag)
                              ?.label
                      } Expenses`}
                      target={metricSummary?.perfect_pnl_expense}
                      actual={metricSummary?.perfect_pnl_expense_actual}
                      changePercent={metricSummary?.perfect_pnl_expense_diff}
                      percentageColor={colorFormat(
                        metricSummary?.perfect_pnl_expense_diff,
                        true,
                      )}
                      symbol={isGoodValue(
                        metricSummary?.perfect_pnl_expense_diff,
                      )}
                      image={
                        "/streamline_money-graph-arrow-decrease-down-stats-graph-descend-right-arrow.png"
                      }
                      detailInfo={getTileText(9, selectedTag, metricSummary)}
                    />
                  </Stack>
                  <Stack width="100%" align="center">
                    <AdvancedMetricUpperCard
                      titleLeft={ytd ? "YTD Target" : "Current Month's Target"}
                      titleRight={ytd ? "YTD Actual Amount" : "Actual Amount"}
                      target={metricSummary?.current_month_target}
                      actual={metricSummary?.actual}
                      changePercent={metricSummary?.current_target_change_pct}
                      percentageColor={colorFormat(
                        metricSummary?.current_target_change_pct,
                        selectedTag === "profit" || selectedTag === "income"
                          ? false
                          : true,
                      )}
                      symbol={isGoodValue(
                        metricSummary?.current_target_change_pct,
                      )}
                      image={"/hugeicons_target-dollar.png"}
                      detailInfo={getTileText(10, selectedTag, metricSummary)}
                    />
                  </Stack>
                  <Stack width="100%" align="center">
                    <AdvancedMetricUpperCard
                      titleLeft="Next Month's Target"
                      titleRight={"Current Month Amount"}
                      target={metricSummary?.next_month_target}
                      actual={metricSummary?.current_month_total}
                      changePercent={
                        metricSummary?.next_month_target_change_pct
                      }
                      percentageColor={colorFormat(
                        metricSummary?.next_month_target_change_pct,
                        selectedTag === "profit" || selectedTag === "income"
                          ? false
                          : true,
                      )}
                      symbol={isGoodValue(
                        metricSummary?.next_month_target_change_pct,
                      )}
                      image={"/tabler_calendar-dollar.png"}
                      detailInfo={getTileText(11, selectedTag, metricSummary)}
                    />
                  </Stack>
                </Grid>
              </VStack>
            )}
            <Text fontWeight={700} fontSize="24px" mb={"8px"}>
              Monthly Historical Comparison
            </Text>

            <SimpleGrid
              columns={{ base: 1, sm: 1, md: 2, lg: 4 }}
              gap={"16px"}
              width="100%"
            >
              <AdvancedMetricsLowerCard
                topTitle={"Previous Month"}
                leftTitle={"Change"}
                rightTitle="% Change"
                detail={true}
                top={metricSummary?.last_month_total}
                left={metricSummary?.change_from_last_month}
                right={metricSummary?.change_from_last_month_pct}
                percentageColor={colorFormat(
                  metricSummary?.change_from_last_month_pct,
                  selectedTag === "profit" || selectedTag === "income"
                    ? false
                    : true,
                )}
                symbol={isGoodValue(metricSummary?.change_from_last_month_pct)}
                detailInfo={getTileText(4, selectedTag, metricSummary)}
              />
              <AdvancedMetricsLowerCard
                topTitle={"3-Month Average"}
                leftTitle={"Change"}
                rightTitle="% Change"
                detail={true}
                top={metricSummary?.last_three_month_avg}
                left={metricSummary?.change_last_three_month_avg}
                right={metricSummary?.change_last_three_month_avg_pct}
                percentageColor={colorFormat(
                  metricSummary?.change_last_three_month_avg_pct,
                  selectedTag === "profit" || selectedTag === "income"
                    ? false
                    : true,
                )}
                symbol={isGoodValue(
                  metricSummary?.change_last_three_month_avg_pct,
                )}
                detailInfo={getTileText(7, selectedTag, metricSummary)}
              />
              <AdvancedMetricsLowerCard
                topTitle={"YTD Average"}
                leftTitle={"Change"}
                rightTitle={"% Change"}
                detail={true}
                top={metricSummary?.ytd_average}
                left={metricSummary?.change_from_ytd_avg}
                right={metricSummary?.change_from_ytd_avg_pct}
                percentageColor={colorFormat(
                  metricSummary?.change_from_ytd_avg_pct,
                  selectedTag === "profit" || selectedTag === "income"
                    ? false
                    : true,
                )}
                symbol={isGoodValue(metricSummary?.change_from_ytd_avg_pct)}
                detailInfo={getTileText(5, selectedTag, metricSummary)}
                isLocked={customer_type === "trial" ? true : false}
              />
              <AdvancedMetricsLowerCard
                topTitle={"Previous Year Average"}
                leftTitle={"Change"}
                rightTitle={"% Change"}
                detail={true}
                top={metricSummary?.last_ytd_average}
                left={metricSummary?.change_from_last_ytd_avg}
                right={metricSummary?.change_from_last_ytd_avg_pct}
                percentageColor={colorFormat(
                  metricSummary?.change_from_last_ytd_avg_pct,
                  selectedTag === "profit" || selectedTag === "income"
                    ? false
                    : true,
                )}
                symbol={isGoodValue(
                  metricSummary?.change_from_last_ytd_avg_pct,
                )}
                detailInfo={getTileText(6, selectedTag, metricSummary)}
                isLocked={customer_type === "trial" ? true : false}
              />
            </SimpleGrid>
            <Stack align={"center"} my={"16px"}>
              <Box width="100%">
                <Card height={"100%"} width={"100%"} borderRadius={"12px"}>
                  <CardBody height={"200px"} padding={0}>
                    <AdvancedMetricsGraph
                      selectedTag={selectedTag}
                      defaultCheck={activeType}
                      customerId={customer_id}
                      formattedDate={formattedDate}
                    />
                  </CardBody>
                </Card>
              </Box>
            </Stack>
            {selectedTag !== "profit" && (
              <Stack>
                <VendorTable
                  customer_id={customer_id}
                  dashDate={dashDate}
                  updateDate={updateDate}
                  dateCategory={dateCategory}
                  setDateCategory={setDateCategory}
                  initialDashDate={initialDashDate}
                  metric={true}
                  tag={selectedTag}
                  h="542px"
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </ChakraProvider>
    </ClientDashboardLayout>
  );
};

export default AdvancedMetrics;
